import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

export const useFileListState = (items, excludedFileIds = []) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [listView, setListView] = useState(false);

  const visibleFiles = useMemo(() => {
    const excludedSet = new Set(excludedFileIds);
    return items.filter((item) => !excludedSet.has(item.id));
  }, [items, excludedFileIds]);

  useEffect(() => {
    setCheckedItems((prevChecked) => {
      const newCheckedItems = Object.fromEntries(
        visibleFiles
          .filter((item) => prevChecked[item.id] !== undefined)
          .map((item) => [item.id, prevChecked[item.id]]),
      );

      return JSON.stringify(prevChecked) === JSON.stringify(newCheckedItems)
        ? prevChecked
        : newCheckedItems;
    });
  }, [JSON.stringify(visibleFiles)]);

  const isChecked = (id) => !!checkedItems[id];

  const toggleItem = (id) => {
    setCheckedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleAll = () => {
    const selectedCount = Object.values(checkedItems).filter(Boolean).length;
    const shouldCheckAll = selectedCount !== visibleFiles.length;

    const newCheckedState = visibleFiles.reduce((acc, item) => {
      acc[item.id] = shouldCheckAll;
      return acc;
    }, {});

    setCheckedItems(newCheckedState);
  };

  const clearAll = () => setCheckedItems({});

  const selectedItems = useMemo(
    () => visibleFiles.filter((item) => !!checkedItems[item.id]),
    [visibleFiles, checkedItems],
  );

  const selectedPurchasedItemIds = useMemo(
    () => selectedItems.map((item) => item.id),
    [selectedItems],
  );

  const selectedCount = selectedItems.length;
  const isSomeChecked = selectedCount > 0;
  const isAllChecked =
    visibleFiles.length > 0 &&
    visibleFiles.every((item) => checkedItems[item.id]) &&
    selectedCount > 0;

  return {
    checkedItems,
    isChecked,
    toggleItem,
    toggleAll,
    clearAll,
    selectedCount,
    isSomeChecked,
    isAllChecked,
    visibleFiles,
    selectedItems,
    selectedPurchasedItemIds,
    listView,
    setListView,
  };
};

export const FileListStateShape = PropTypes.shape({
  checkedItems: PropTypes.object.isRequired,
  isChecked: PropTypes.func.isRequired,
  toggleItem: PropTypes.func.isRequired,
  toggleAll: PropTypes.func.isRequired,
  clearAll: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
  isSomeChecked: PropTypes.bool.isRequired,
  isAllChecked: PropTypes.bool.isRequired,
  visibleFiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPurchasedItemIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  listView: PropTypes.bool.isRequired,
  setListView: PropTypes.func.isRequired,
});
